import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Tooltip,
  Typography
} from "@material-ui/core";
import RefreshIcon from "@material-ui/icons/Refresh";
import WarningIcon from "@material-ui/icons/Warning";
import axios from "axios";
import dateFormat from "dateformat";
import api from "../api";
import Chart from "../../../utils/chart";
import colors from "../../../utils/colors";

const EXPOSURE_STATUS = {
  green: { color: "#17c671", label: "Dentro de los límites" },
  yellow: { color: "#f5a623", label: "Primer límite excedido" },
  red: { color: "#c4183c", label: "Segundo límite excedido" },
  unknown: { color: "#adb5bd", label: "Sin datos de exposición" }
};

const formatMoney = value => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString("es-AR", {
    style: "currency",
    currency: "ARS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const formatPercent = value => {
  if (value === null || value === undefined || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num;
};


const getExposureStatus = data => {
  if (!data) return "unknown";
  if (data.has_exceeded_second_limit) return "red";
  if (data.has_exceeded_first_limit) return "yellow";
  return "green";
};

const clientLabel = row => {
  const name = (row.business_name || "").trim();
  if (name) return name;
  return row.client_id ? `Cliente #${row.client_id}` : "—";
};

const UsageBar = ({ percentage, exceeded, limit }) => {
  const pct = formatPercent(percentage);
  const limitNum = Number(limit) || 0;

  if (limitNum <= 0) {
    return (
      <Typography variant="caption" color="textSecondary">
        Sin límite
      </Typography>
    );
  }
  if (pct === null) {
    return (
      <Typography variant="caption" color="textSecondary">
        —
      </Typography>
    );
  }

  const displayPct = Math.min(Math.max(pct, 0), 100);
  const fillColor = exceeded
    ? "#c4183c"
    : displayPct >= 90
      ? "#f5a623"
      : displayPct >= 75
        ? "#674eec"
        : "#17c671";

  return (
    <Box minWidth={100}>
      <Box display="flex" justifyContent="space-between" mb={0.5}>
        <Typography
          variant="caption"
          style={{
            fontWeight: exceeded ? 600 : 500,
            color: exceeded ? "#c4183c" : "inherit"
          }}
        >
          {displayPct.toFixed(1)}%
        </Typography>
        {exceeded && <WarningIcon style={{ fontSize: 14, color: "#c4183c" }} />}
      </Box>
      <Box height={6} borderRadius={3} bgcolor="#e9ecef" overflow="hidden">
        <Box
          height="100%"
          width={`${displayPct}%`}
          borderRadius={3}
          bgcolor={fillColor}
        />
      </Box>
    </Box>
  );
};

const CreditLimitChart = ({ label, limit, available, percentage, exceeded }) => {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const limitNum = Number(limit) || 0;

  useEffect(() => {
    if (!canvasRef.current || limitNum <= 0) return;

    const availableNum = Math.max(0, Number(available) || 0);
    const used = Math.max(0, limitNum - availableNum);
    const chartColor = exceeded
      ? colors.salmon.toRGBA(0.85)
      : colors.primary.toRGBA(0.85);

    chartRef.current = new Chart(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: ["Utilizado", "Disponible"],
        datasets: [
          {
            data: [used, availableNum],
            backgroundColor: [chartColor, colors.success.toRGBA(0.75)],
            borderWidth: 0
          }
        ]
      },
      options: {
        legend: { position: "bottom" },
        cutoutPercentage: 65
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [label, limit, available, percentage, exceeded, limitNum]);

  if (limitNum <= 0) {
    return (
      <Typography variant="caption" color="textSecondary" align="center" display="block">
        Sin límite configurado
      </Typography>
    );
  }

  const pct = formatPercent(percentage);

  return (
    <Box textAlign="center" py={1}>
      <Typography variant="caption" display="block" style={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="textSecondary" display="block">
        {formatMoney(limit)}
        {pct !== null ? ` · ${pct.toFixed(1)}%` : ""}
      </Typography>
      <canvas ref={canvasRef} height={100} />
      <Typography variant="caption">Disp. {formatMoney(available)}</Typography>
    </Box>
  );
};

const StatusDot = ({ status, onClick }) => {
  const { color, label } = EXPOSURE_STATUS[status];

  return (
    <Tooltip title={`${label} — clic para detalle`}>
      <Box
        onClick={onClick}
        component="span"
        display="inline-block"
        width={14}
        height={14}
        borderRadius="50%"
        style={{
          cursor: "pointer",
          backgroundColor: color,
          boxShadow: `0 0 0 2px #fff, 0 0 0 3px ${color}`,
          verticalAlign: "middle"
        }}
        role="button"
        aria-label={label}
      />
    </Tooltip>
  );
};

const DetailField = ({ label, value, highlight }) => (
  <Grid item xs={6} sm={4}>
    <Typography variant="caption" color="textSecondary" display="block">
      {label}
    </Typography>
    <Typography
      variant="body2"
      style={highlight ? { fontWeight: 600, color: "#c4183c" } : {}}
    >
      {value}
    </Typography>
  </Grid>
);

const CreditLimitExposure = ({ clientId }) => {
  const [exposure, setExposure] = useState(null);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshConfirm, setRefreshConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);

  const fetchExposure = async () => {
    if (!clientId) return null;
    const token = localStorage.getItem("userToken");
    const url = `${api.clients().clientExposures()}?client_id=${clientId}`;
    const response = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const raw = Array.isArray(response.data)
      ? response.data[0]
      : response.data.data
        ? response.data.data[0]
        : response.data;
    setExposure(raw);
    return raw;
  };

  useEffect(() => {
    if (!clientId) {
      setExposure(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetchExposure()
      .catch(() => {
        if (!cancelled) setExposure(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleOpenModal = () => {
    if (exposure) {
      setModalOpen(true);
      setRefreshConfirm(false);
      setRefreshError(null);
    }
  };

  const handleCloseModal = () => {
    if (!refreshing) {
      setModalOpen(false);
      setRefreshConfirm(false);
      setRefreshError(null);
    }
  };

  const handleRefreshConfirm = async () => {
    if (!clientId) return;
    const token = localStorage.getItem("userToken");
    setRefreshing(true);
    setRefreshError(null);
    try {
      await axios.post(
        api.clients().refreshExposures(),
        { clientIds: [clientId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchExposure();
      setRefreshConfirm(false);
    } catch (err) {
      const resData = err.response && err.response.data ? err.response.data : {};
      setRefreshError(
        resData.message ||
          resData.error ||
          "No se pudo actualizar el estado de cuenta."
      );
    } finally {
      setRefreshing(false);
    }
  };

  if (!clientId) return null;

  if (loading) {
    return <CircularProgress size={18} style={{ marginLeft: 8 }} />;
  }

  const status = getExposureStatus(exposure);

  if (!exposure) {
    return (
      <Tooltip title="Sin datos de exposición">
        <Box
          display="inline-flex"
          alignItems="center"
          ml={1}
          width={20}
          height={20}
          borderRadius="50%"
          bgcolor="#e9ecef"
        />
      </Tooltip>
    );
  }

  const exceeded =
    exposure.has_exceeded_first_limit || exposure.has_exceeded_second_limit;

  return (
    <>
      <Box display="inline-flex" alignItems="center" ml={1} component="span">
        <StatusDot status={status} onClick={handleOpenModal} />
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle disableTypography>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h6">Estado de cuenta</Typography>
              <Typography variant="body2" color="textSecondary">
                {clientLabel(exposure)}
                {exposure.sap_code ? ` · SAP ${exposure.sap_code}` : ""}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={EXPOSURE_STATUS[status].label}
              style={{
                backgroundColor: EXPOSURE_STATUS[status].color,
                color: "#fff"
              }}
            />
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {exposure.updated_at && (
            <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
              Actualizado {dateFormat(exposure.updated_at, "dd/mm/yyyy HH:MM")}
            </Typography>
          )}

          <Typography variant="overline" color="textSecondary">
            Saldos y exposición
          </Typography>
          <Grid container spacing={2} className="mb-3">
            <DetailField label="Saldo SAP" value={formatMoney(exposure.sap_balance)} />
            <DetailField
              label="OTs pendientes"
              value={formatMoney(exposure.pending_work_orders)}
            />
            <DetailField
              label="Exposición total"
              value={formatMoney(exposure.total_exposure)}
              highlight={exceeded}
            />
          </Grid>

          <Grid container spacing={2} className="mb-3">
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                1er límite
              </Typography>
              <UsageBar
                percentage={exposure.percentage_of_first_credit_limit}
                exceeded={exposure.has_exceeded_first_limit}
                limit={exposure.first_credit_limit}
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Disp. {formatMoney(exposure.available_first_credit)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="caption" color="textSecondary">
                2do límite
              </Typography>
              <UsageBar
                percentage={exposure.percentage_of_second_credit_limit}
                exceeded={exposure.has_exceeded_second_limit}
                limit={exposure.second_credit_limit}
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Disp. {formatMoney(exposure.available_second_credit)}
              </Typography>
            </Grid>
          </Grid>

          <Box display="flex" justifyContent="space-around">
            <CreditLimitChart
              label="1er límite"
              limit={exposure.first_credit_limit}
              available={exposure.available_first_credit}
              percentage={exposure.percentage_of_first_credit_limit}
              exceeded={exposure.has_exceeded_first_limit}
            />
            <CreditLimitChart
              label="2do límite"
              limit={exposure.second_credit_limit}
              available={exposure.available_second_credit}
              percentage={exposure.percentage_of_second_credit_limit}
              exceeded={exposure.has_exceeded_second_limit}
            />
          </Box>

          {refreshConfirm && (
            <DialogContentText className="mt-2">
              ¿Recalcular el estado de cuenta de <strong>{clientLabel(exposure)}</strong>?
            </DialogContentText>
          )}
          {refreshError && (
            <Typography variant="body2" color="error" className="mt-2">
              {refreshError}
            </Typography>
          )}
        </DialogContent>

        <DialogActions>
          {refreshConfirm ? (
            <>
              <Button onClick={() => setRefreshConfirm(false)} disabled={refreshing}>
                Cancelar
              </Button>
              <Button
                color="primary"
                variant="contained"
                onClick={handleRefreshConfirm}
                disabled={refreshing}
                startIcon={
                  refreshing ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
              >
                Confirmar
              </Button>
            </>
          ) : (
            <>
              <Button
                color="primary"
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={() => setRefreshConfirm(true)}
                disabled={refreshing}
              >
                Actualizar
              </Button>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                href={`/client-exposures`}
              >
                Ver listado
              </Button>
              <Button onClick={handleCloseModal} color="primary" size="small">
                Cerrar
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreditLimitExposure;
